/*
 * useAdminDemoMode — admin-only "Demo Mode" toggle used for sales-demo cheat
 * codes (assessment auto-complete + AI Opportunity Score auto-fill).
 *
 * Distinct from the existing `useDemoMode` URL-param hook (which gates public
 * landing-page demo content). This one:
 *
 *   1. role-eligibility — super_admin / consultant / admin only.
 *   2. session toggle    — must be actively flipped on by the consultant.
 *
 * Both must be true → demo-mode features attach.
 */
import { useEffect, useState } from "react";

const STORAGE_KEY = "clarity_admin_demo_mode_on";

const ELIGIBLE_ROLES = new Set([
  "super_admin",
  "consultant",
  // 'admin' is the legacy alias used in this codebase for super-user dirk
  "admin",
]);

export function isAdminDemoEligibleRole(role) {
  return ELIGIBLE_ROLES.has(role);
}

export function getAdminDemoModeStored() {
  if (typeof window === "undefined") return false;
  try {
    return window.localStorage.getItem(STORAGE_KEY) === "1";
  } catch {
    return false;
  }
}

export function setAdminDemoModeStored(on) {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(STORAGE_KEY, on ? "1" : "0");
    window.dispatchEvent(new Event("clarity-admin-demo-mode-changed"));
  } catch {}
}

/**
 * useAdminDemoMode(session) → { eligible, on, setOn }
 *
 * SIMPLIFIED: gate is now ONLY the localStorage flag. We dropped the role
 * check because role-shape detection across portals proved unreliable in the
 * wild. Demo features fully attach when `clarity_admin_demo_mode_on === "1"`.
 * Prospects never have that flag, so they never see anything. If they manually
 * set the flag in DevTools they get… a dummy autofill button on a public
 * form. Zero real-data exposure.
 */
export function useAdminDemoMode(_session) {
  const eligible = true;
  const [on, setOnState] = useState(() => getAdminDemoModeStored());

  useEffect(() => {
    const sync = () => setOnState(getAdminDemoModeStored());
    sync();
    window.addEventListener("storage", sync);
    window.addEventListener("clarity-admin-demo-mode-changed", sync);
    return () => {
      window.removeEventListener("storage", sync);
      window.removeEventListener("clarity-admin-demo-mode-changed", sync);
    };
  }, []);

  const setOn = (val) => {
    setAdminDemoModeStored(val);
    setOnState(val);
  };

  return { eligible, on, setOn };
}

export function useIsAdminDemoActive(session) {
  const { on } = useAdminDemoMode(session);
  return on;
}

/**
 * Audit-log emitter. Console one-liner + fire-and-forget POST to
 * /api/admin/demo/audit. Never throws.
 */
export function logAdminDemoEvent({ feature, session, extra = {} }) {
  try {
    const role = session?.user?.role || "unknown";
    const email = session?.user?.email || "unknown";
    // eslint-disable-next-line no-console
    console.log(`[DEMO] Feature triggered: ${feature} | Role: ${role} | User: ${email}`, extra);
    const API = process.env.REACT_APP_BACKEND_URL;
    if (!API) return;
    fetch(`${API}/api/admin/demo/audit`, {
      method: "POST",
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
        "x-user-id": session?.user?.id || "",
        "x-user-role": role,
        "x-tenant-id": session?.tenant?.id || "tenant_1",
      },
      body: JSON.stringify({ feature, extra }),
    }).catch(() => {});
  } catch {}
}
